import { BookOpenText, Copy, Lightbulb, Mail, Megaphone, MessageSquareWarning, Phone, Send } from "lucide-react";
import { useRef, useState, type FormEvent, type MouseEvent } from "react";

import { versioneApp } from "../lib/app-version";
import { valoreDelCampo } from "../lib/field-value";
import { MAX_MAILTO_LENGTH, preparedEmailHref, preparedEmailText, waitForMailApp } from "../lib/order-email";
import { numeroPerChiamata } from "../../shared/support-contacts";
import { isOfflineQueued } from "../lib/offline-link";
import { trpc, type UserSession } from "../lib/trpc";
import { DOMANDE, GUIDA } from "../lib/support-content";
import { useActiveModules, visibileConModuli } from "../lib/active-modules";

/**
 * Indirizzo di riserva, usato solo finche i recapiti veri non sono arrivati dal server.
 * La verita sta in banca dati e si cambia dalla console, senza ripubblicare l'app.
 */
export const SUPPORT_EMAIL = "support.manutio@cristianbedin.it";



const tipiSegnalazione = [
  { valore: "idea" as const, etichetta: "Idea nuova" },
  { valore: "change" as const, etichetta: "Modifica a qualcosa di esistente" },
  { valore: "issue" as const, etichetta: "Segnalazione di problema" },
];

/** Oltre questa attesa si smette di aspettare e si dice all'utente cosa e successo. */
export const ATTESA_MASSIMA_INVIO_MS = 15_000;

/** Sotto questa soglia il server rifiuta la segnalazione: meglio dirlo qui che farsi dire di no da lontano. */
export const LUNGHEZZA_MINIMA_SEGNALAZIONE = 5;

export class RispostaMancante extends Error {
  constructor() {
    super(
      "Non e arrivata la conferma dal server entro quindici secondi. La segnalazione potrebbe essere stata salvata lo stesso: "
      + "prima di riscriverla, avvisaci, cosi evitiamo di trovarla doppia.",
    );
    this.name = "RispostaMancante";
  }
}

function attesaMassima() {
  return new Promise<never>((_, rifiuta) => {
    setTimeout(() => rifiuta(new RispostaMancante()), ATTESA_MASSIMA_INVIO_MS);
  });
}

function messaggioErrore(errore: unknown) {
  const dettaglio = errore instanceof Error && errore.message ? ` Dettaglio: ${errore.message}` : "";
  return `Non sono riuscito a registrare la segnalazione.${dettaglio} Riprova, e se continua scrivici dal riquadro qui sopra.`;
}

export type SupportPageOptions = {
  session: UserSession;
  /** Iniettabili per le prove: in esecuzione vera sono il browser e gli appunti di sistema. */
  versione?: string;
  apriPosta?: (href: string) => void;
  attendiPosta?: () => Promise<boolean>;
  copia?: (testo: string) => Promise<void>;
};

function apriPostaDelBrowser(href: string) {
  window.location.assign(href);
}

function copiaNegliAppunti(testo: string) {
  if (!navigator.clipboard?.writeText) return Promise.reject(new Error("appunti non disponibili"));
  return navigator.clipboard.writeText(testo);
}

export function SupportPage(options: SupportPageOptions) {
  const {
    session,
    versione = versioneApp(),
    apriPosta = apriPostaDelBrowser,
    attendiPosta = () => waitForMailApp(),
    copia = copiaNegliAppunti,
  } = options;
  // La guida segue quello che l'azienda ha davvero: finche i moduli non sono noti,
  // si mostra solo il perimetro base, che vale per tutti.
  const { attivi } = useActiveModules();
  const guidaVisibile = visibileConModuli(GUIDA, attivi);
  const domandeVisibili = visibileConModuli(DOMANDE, attivi);
  const [ripiegoPosta, setRipiegoPosta] = useState<string | null>(null);
  const [esitoCopia, setEsitoCopia] = useState<"copiato" | "fallito" | null>(null);
  const [oggetto, setOggetto] = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [contattoErrore, setContattoErrore] = useState<string | null>(null);
  const [tipo, setTipo] = useState<(typeof tipiSegnalazione)[number]["valore"]>("idea");
  const [ideaTesto, setIdeaTesto] = useState("");
  const [ideaInviata, setIdeaInviata] = useState(false);
  const [ideaErrore, setIdeaErrore] = useState<string | null>(null);
  const [invioInCorso, setInvioInCorso] = useState(false);
  const [urgenteEsito, setUrgenteEsito] = useState<string | null>(null);
  const inviaIdea = trpc.feedback.add.useMutation();
  const inviaUrgente = trpc.support.urgente.useMutation();
  // I recapiti stanno in banca dati e si cambiano dalla console: un numero che cambia non
  // deve costringere a ripubblicare l'app. Finche la risposta non arriva vale quello scritto
  // nel programma, cosi la schermata non resta senza indirizzo.
  const contatti = trpc.support.contatti.useQuery();
  const indirizzoSupporto = contatti.data?.email || SUPPORT_EMAIL;
  const telefonoSupporto = contatti.data?.telefono ?? "";
  const puoScrivereUrgente = session.membership.role !== "collaborator";
  // I campi si rileggono dal DOM al momento dell'invio: vedere `valoreDelCampo`.
  const oggettoRef = useRef<HTMLInputElement>(null);
  const messaggioRef = useRef<HTMLTextAreaElement>(null);
  const raccontoRef = useRef<HTMLTextAreaElement>(null);

  const emailHref = preparedEmailHref({
    to: indirizzoSupporto,
    subject: oggetto.trim() || "Richiesta di assistenza MANUTIO",
    body: messaggio.trim(),
  });

  function scriviContatto(aggiorna: () => void) {
    aggiorna();
    setRipiegoPosta(null);
    setEsitoCopia(null);
    setContattoErrore(null);
  }

  /**
   * Un mailto su un dispositivo senza posta configurata non apre niente e non dice niente.
   * Qui si guarda se qualcosa si e davvero aperto e, in caso contrario, si mette in chiaro
   * indirizzo e testo: la richiesta di aiuto non puo perdersi nel silenzio.
   *
   * Il controllo su cosa e stato scritto avviene qui, leggendo i campi veri, e non
   * spegnendo il comando prima: un comando spento non spiega cosa manca.
   */
  async function preparaEmail(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    setEsitoCopia(null);

    const oggettoScritto = valoreDelCampo(oggettoRef.current, oggetto);
    const messaggioScritto = valoreDelCampo(messaggioRef.current, messaggio);
    const mancante = !oggettoScritto ? "l'oggetto" : !messaggioScritto ? "il messaggio" : null;
    if (mancante) {
      setRipiegoPosta(null);
      setContattoErrore(`Scrivete ${mancante} prima di preparare l'email.`);
      return;
    }

    setContattoErrore(null);
    const email = { to: indirizzoSupporto, subject: oggettoScritto, body: messaggioScritto };
    const href = preparedEmailHref(email);
    const testo = preparedEmailText(email);
    // Oltre il limite il collegamento viene troncato o ignorato: non vale la pena tentare.
    if (href.length > MAX_MAILTO_LENGTH) {
      setRipiegoPosta(testo);
      return;
    }

    setRipiegoPosta(null);
    apriPosta(href);
    if (!(await attendiPosta())) setRipiegoPosta(testo);
  }

  async function copiaEmailPronta() {
    if (!ripiegoPosta) return;
    try {
      await copia(ripiegoPosta);
      setEsitoCopia("copiato");
    } catch {
      setEsitoCopia("fallito");
    }
  }

  /**
   * La richiesta parte dal server e non dalla posta del dispositivo.
   *
   * Il "Prepara l'email" resta sotto, come ripiego: se la posta automatica non e ancora
   * configurata, il messaggio resta comunque registrato e la schermata dice di telefonare.
   */
  async function mandaUrgente(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const oggettoScritto = valoreDelCampo(oggettoRef.current, oggetto);
    const messaggioScritto = valoreDelCampo(messaggioRef.current, messaggio);
    const mancante = !oggettoScritto ? "l'oggetto" : !messaggioScritto ? "il messaggio" : null;
    if (mancante) {
      setUrgenteEsito(null);
      setContattoErrore(`Scrivete ${mancante} prima di inviare.`);
      return;
    }

    setContattoErrore(null);
    setUrgenteEsito(null);
    try {
      const risposta = await inviaUrgente.mutateAsync({ subject: oggettoScritto, body: messaggioScritto });
      if (isOfflineQueued(risposta)) {
        setUrgenteEsito("Richiesta salvata sul dispositivo. Parte da sola appena torna la rete. Se e urgente, telefonate.");
        return;
      }
      if (!risposta) return;
      setOggetto("");
      setMessaggio("");
      if (oggettoRef.current) oggettoRef.current.value = "";
      if (messaggioRef.current) messaggioRef.current.value = "";
      setUrgenteEsito(
        risposta.inviata
          ? `Inviata a ${risposta.destinatario}. Vi rispondiamo appena possibile.`
          : risposta.stato === "preview"
            ? `Richiesta registrata per ${risposta.destinatario}. La posta automatica non e ancora attiva: se e urgente, telefonate.`
            : `Non sono riuscito a inviarla${risposta.errore ? `: ${risposta.errore}` : ""}. Resta registrata: se e urgente, telefonate.`,
      );
    } catch (errore) {
      setContattoErrore(
        errore instanceof Error
          ? `Non sono riuscito a inviarla. Dettaglio: ${errore.message}`
          : "Non sono riuscito a inviarla. Riprovate, oppure preparate l'email qui sotto.",
      );
    }
  }

  /** Dopo un invio riuscito il campo va svuotato anche se il testo era arrivato senza evento. */
  function svuotaRacconto() {
    setIdeaTesto("");
    if (raccontoRef.current) raccontoRef.current.value = "";
  }

  async function salvaIdea(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invioInCorso) return;

    // Si crede al campo, non allo stato: con la dettatura il testo c'e ma React non lo sa.
    const racconto = valoreDelCampo(raccontoRef.current, ideaTesto);
    setIdeaInviata(false);
    if (racconto.length === 0) {
      setIdeaErrore("Racconta qualcosa prima di inviare.");
      return;
    }
    if (racconto.length < LUNGHEZZA_MINIMA_SEGNALAZIONE) {
      setIdeaErrore("Scrivi qualche parola in piu, cosi si capisce di cosa si tratta.");
      return;
    }

    setIdeaErrore(null);
    setInvioInCorso(true);
    try {
      await Promise.race([
        inviaIdea.mutateAsync({ kind: tipo, message: racconto }),
        attesaMassima(),
      ]);
      svuotaRacconto();
      setIdeaInviata(true);
    } catch (errore) {
      // Senza questo, una risposta che non torna lascerebbe il pulsante a girare per sempre.
      setIdeaErrore(errore instanceof RispostaMancante ? errore.message : messaggioErrore(errore));
    } finally {
      setInvioInCorso(false);
    }
  }

  return (
    <main className="page support-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Assistenza</p>
          <h1>Supporto</h1>
          <p>Come funziona l'app, le domande più frequenti e i due modi per farci sapere qualcosa.</p>
        </div>
      </header>

      <MessaggiRicevuti />

      <section className="support-block" aria-labelledby="guida-titolo">
        <h2 id="guida-titolo"><BookOpenText size={20} aria-hidden="true" /> Guida, come funziona l'app</h2>
        <div className="support-guide">
          {guidaVisibile.map((voce) => (
            <article key={voce.label}>
              <h3>{voce.label}</h3>
              <p>{voce.testo}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="support-block" aria-labelledby="faq-titolo">
        <h2 id="faq-titolo">Domande frequenti</h2>
        <div className="support-faq">
          {domandeVisibili.map((voce) => (
            <details key={voce.label}>
              <summary>{voce.label}</summary>
              <p>{voce.risposta}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="support-block" aria-labelledby="contatto-titolo">
        <h2 id="contatto-titolo"><Mail size={20} aria-hidden="true" /> Contatto per un problema urgente</h2>
        <p className="support-block__intro">
          Per le cose che non possono aspettare.{" "}
          {puoScrivereUrgente
            ? "Scrivete qui sotto e la richiesta parte subito, oppure telefonate."
            : "Parlatene con il titolare, oppure usate direttamente questi recapiti."}
        </p>
        <div className="support-recapiti">
          {telefonoSupporto && (
            <a className="button button--outline" href={`tel:${numeroPerChiamata(telefonoSupporto)}`}>
              <Phone size={17} aria-hidden="true" /> {telefonoSupporto}
            </a>
          )}
          <a className="button button--outline" href={`mailto:${indirizzoSupporto}`}>
            <Mail size={17} aria-hidden="true" /> {indirizzoSupporto}
          </a>
        </div>
        {puoScrivereUrgente && (
        <form className="support-form" onSubmit={mandaUrgente}>
          <label>
            <span>Oggetto</span>
            <input
              ref={oggettoRef}
              value={oggetto}
              onChange={(event) => scriviContatto(() => setOggetto(event.target.value))}
              placeholder="Non riesco a chiudere un intervento"
              maxLength={120}
            />
          </label>
          <label>
            <span>Messaggio</span>
            <textarea
              ref={messaggioRef}
              value={messaggio}
              onChange={(event) => scriviContatto(() => setMessaggio(event.target.value))}
              rows={5}
              placeholder="Raccontate cosa succede e cosa stavate facendo."
            />
          </label>
          {contattoErrore && <p className="form-error" role="alert">{contattoErrore}</p>}
          {urgenteEsito && <p className="support-form__ok" role="status">{urgenteEsito}</p>}
          <div className="form-actions">
            {/* Sempre acceso: si controlla al momento dell'invio e si dice cosa manca. */}
            <button className={`button button--primary${inviaUrgente.isPending ? " button--saving" : ""}`} disabled={inviaUrgente.isPending}>
              <Send size={17} aria-hidden="true" /> {inviaUrgente.isPending ? "Invio..." : "Invia la richiesta"}
            </button>
            {/* Ripiego di sempre: se la posta automatica non e attiva, l'email si prepara a mano. */}
            <a className="button" href={emailHref} onClick={preparaEmail}>
              Prepara l'email
            </a>
          </div>
        </form>
        )}
        {ripiegoPosta && (
          <div className="support-fallback" role="group" aria-label="Testo pronto da copiare">
            <p>
              Non si e aperta nessuna applicazione di posta. Scrivete a <strong>{SUPPORT_EMAIL}</strong> da dove
              preferite, copiando il testo qui sotto.
            </p>
            <label>
              <span>Email pronta</span>
              <textarea readOnly rows={6} value={ripiegoPosta} />
            </label>
            <div className="form-actions">
              <button type="button" className="button" onClick={copiaEmailPronta}>
                <Copy size={16} aria-hidden="true" /> Copia indirizzo e testo
              </button>
            </div>
            {esitoCopia === "copiato" && <p className="support-form__ok" role="status">Copiato.</p>}
            {esitoCopia === "fallito" && (
              <p className="form-error" role="alert">
                Non sono riuscito a copiare negli appunti. Selezionate il testo qui sopra e copiatelo a mano.
              </p>
            )}
          </div>
        )}
      </section>

      <section className="support-block" aria-labelledby="idee-titolo">
        <h2 id="idee-titolo"><Lightbulb size={20} aria-hidden="true" /> Idee e segnalazioni</h2>
        <p className="support-block__intro">
          Qui finisce tutto quello che vi viene in mente usando l'app: una funzione che manca, qualcosa da cambiare,
          un difetto che avete notato. Non è una richiesta urgente, è materiale per il prossimo giro di lavoro.
        </p>
        <form className="support-form" onSubmit={salvaIdea}>
          <label>
            <span>Di cosa si tratta</span>
            <select value={tipo} onChange={(event) => setTipo(event.target.value as typeof tipo)}>
              {tipiSegnalazione.map((voce) => (
                <option key={voce.valore} value={voce.valore}>{voce.etichetta}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Racconta</span>
            <textarea
              ref={raccontoRef}
              value={ideaTesto}
              onChange={(event) => {
                setIdeaTesto(event.target.value);
                setIdeaInviata(false);
                setIdeaErrore(null);
              }}
              rows={5}
              placeholder="Sarebbe comodo vedere subito quante ore ha fatto ogni collaboratore nella settimana."
              maxLength={2000}
            />
          </label>
          {ideaErrore && <p className="form-error" role="alert">{ideaErrore}</p>}
          {ideaInviata && <p className="support-form__ok" role="status"><MessageSquareWarning size={16} aria-hidden="true" /> Registrata, grazie. La leggeremo quando prepareremo le prossime modifiche.</p>}
          <div className="form-actions">
            {/* Sempre attivo: si controlla al momento dell'invio e si dice cosa manca. */}
            <button className="button button--primary button--saving" disabled={invioInCorso}>
              {invioInCorso ? "Salvataggio..." : "Invia"}
            </button>
          </div>
        </form>

        <IdeeGiaScritte />
      </section>

      {/* Serve a una domanda sola, quando un problema si vede a distanza: che versione leggi? */}
      <p className="support-version">Versione {versione}</p>
    </main>
  );
}

function quandoInData(data: Date | string) {
  return new Date(data).toLocaleDateString("it-IT");
}

/**
 * I messaggi gia ricevuti da chi ha costruito il programma.
 *
 * L'avviso all'apertura si chiude confermando la lettura, e da quel momento sparisce: chi lo
 * chiude e poi si accorge di non aver capito bene deve poterlo ritrovare, e questo e il posto.
 */
function MessaggiRicevuti() {
  const archivio = trpc.announcements.archivio.useQuery();
  const voci = archivio.data ?? [];
  if (voci.length === 0) return null;

  return (
    <section className="support-block" aria-labelledby="messaggi-titolo">
      <h2 id="messaggi-titolo"><Megaphone size={20} aria-hidden="true" /> Messaggi ricevuti</h2>
      <div className="support-faq">
        {voci.map((voce) => (
          <details key={voce.announcementId}>
            <summary>
              {voce.title} <span className="support-messaggi__data">· {quandoInData(voce.createdAt)}</span>
            </summary>
            <p>{voce.body}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** Le idee gia mandate, con lo stato: serve a non riscrivere due volte la stessa cosa. */
function IdeeGiaScritte() {
  const idee = trpc.support.idee.useQuery();
  const voci = idee.data ?? [];
  if (voci.length === 0) return null;

  return (
    <div className="support-idee">
      <h3>Quelle che avete gia mandato</h3>
      <ul>
        {voci.map((voce) => (
          <li key={voce.id}>
            <span className="support-idee__stato">{voce.handledAt ? "✅" : "⏳"}</span>
            <span className="support-idee__testo">
              <strong>{voce.message}</strong>
              <small>{voce.authorName} · {quandoInData(voce.createdAt)}{voce.handledAt ? " · presa in carico" : ""}</small>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
